/**
 * ChooseTemplateStep - single Step 1 of the drafting wizard.
 *
 * Replaces the older two-step "pick type" + "pick template" flow. PONS
 * document types and the user's own structure templates are surfaced as a
 * single mutually-exclusive choice via a two-tab layout. Either side can
 * be previewed without committing the selection.
 *
 * Selection shape reported up:
 *   - PONS tab        → { document_type_id, structure_template_id: '' }
 *   - Your templates  → { document_type_id: tmpl.DraftType || fallback,
 *                          structure_template_id: tmpl.id }
 */

import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  PiFileTextLight, PiCheckLight, PiSparkleLight, PiEyeLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import SearchInput from '../../shared/TailwindComponents/SearchInput';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { Well } from '../../shared/brand/Field';
import { FOCUS_RING } from '../../shared/TailwindComponents/FieldShell';
import { SELECTED_CHECK_DISC, SELECTED_CHIP, SELECTED_WELL } from '../../shared/brand/selectedCard';
import { useAppDispatch, useAppSelector, RootState } from '../../../store';
import { fetchStructureTemplates } from '../../../store/actions/TemplateActions';
import { fetchDraftingTypes, DraftingKind, DraftingType } from '../../../store/slices/draftingSlice';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import apis from '../../../utils/apis';

export interface OwnStructureTemplate {
  id: string;
  TemplateName: string;
  DraftType?: string;
  is_global?: boolean;
  status?: string;
  Structure?: { description?: string; outline?: any[] };
}

export interface ChooseTemplateSelection {
  /** Always set on submit - used as the document_type_id sent to backend. */
  document_type_id: string;
  /** Set only when the user picked one of their own templates. */
  structure_template_id: string;
  /** Free-form display label for downstream steps (defaults to type/template name). */
  display_name: string;
}

type Tab = 'pons' | 'own';

// Turn backend slugs (e.g. "compliance_reports") into human labels
// ("Compliance Reports"). Idempotent on already-formatted names —
// "Case Chronology" and "PONS" pass through unchanged.
const humanizeLabel = (raw: string): string =>
  (raw || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface ChooseTemplateStepProps {
  kind: DraftingKind;
  value: ChooseTemplateSelection;
  onChange: (next: ChooseTemplateSelection) => void;
}

const ChooseTemplateStep: React.FC<ChooseTemplateStepProps> = ({ kind, value, onChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const ponsTypes = useAppSelector((s: RootState) => s.drafting.types[kind]) as DraftingType[];
  const ponsLoading = useAppSelector((s: RootState) => s.drafting.typesLoading[kind]) as boolean;
  const ponsError = useAppSelector((s: RootState) => s.drafting.typesError[kind]) as string | null;

  const [ownTemplates, setOwnTemplates] = useState<OwnStructureTemplate[]>([]);
  const [ownLoading, setOwnLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('pons');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (ponsTypes.length === 0 && !ponsLoading) {
      dispatch(fetchDraftingTypes({ kind }));
    }
  }, [dispatch, kind, ponsTypes.length, ponsLoading]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setOwnLoading(true);
      try {
        const res: any = await dispatch(fetchStructureTemplates({ page_size: 200 }) as any);
        if (!alive) return;
        const list: OwnStructureTemplate[] = (res?.templates ?? []).filter(
          (tmpl: OwnStructureTemplate) =>
            !tmpl.is_global && (!tmpl.status || tmpl.status === 'completed' || tmpl.status === 'ready'),
        );
        setOwnTemplates(list);
      } finally {
        if (alive) setOwnLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dispatch]);

  // PONS categorisation
  const ponsCategories = useMemo(
    () => Array.from(new Set(ponsTypes.map((ty) => ty.category || 'Other'))).sort((a, b) => a.localeCompare(b)),
    [ponsTypes],
  );
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredPons = useMemo(() => {
    const q = search.toLowerCase().trim();
    return ponsTypes.filter((ty) => {
      const matchesCat = !activeCategory || (ty.category || 'Other') === activeCategory;
      const matchesQuery =
        !q ||
        ty.name.toLowerCase().includes(q) ||
        (ty.description ?? '').toLowerCase().includes(q);
      return matchesCat && matchesQuery;
    });
  }, [ponsTypes, activeCategory, search]);

  const filteredOwn = useMemo(() => {
    const q = search.toLowerCase().trim();
    return q ? ownTemplates.filter((t) => t.TemplateName.toLowerCase().includes(q)) : ownTemplates;
  }, [ownTemplates, search]);

  const switchTab = (next: Tab) => {
    if (next === tab) return;
    // Clear any selection from the other tab to keep the choice mutually exclusive.
    if (value.document_type_id || value.structure_template_id) {
      onChange({ document_type_id: '', structure_template_id: '', display_name: '' });
    }
    setTab(next);
    setActiveCategory(null);
    setSearch('');
  };

  const selectPons = (ty: DraftingType) => {
    onChange({
      document_type_id: ty.document_type_id,
      structure_template_id: '',
      display_name: ty.name,
    });
  };

  const selectOwn = (tmpl: OwnStructureTemplate) => {
    onChange({
      // Backend route prefers the template's intended draft type so the AI
      // engine loads the right base/rules instruction blobs; the structure
      // template's markdown still overrides template.md.
      document_type_id: tmpl.DraftType || `template_${tmpl.id}`,
      structure_template_id: tmpl.id,
      display_name: tmpl.TemplateName,
    });
  };

  const ponsIcon = (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-sunken text-fg-muted">
      <PiSparkleLight size={18} />
    </span>
  );
  const ownIcon = (
    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-sunken text-fg-muted">
      <PiFileTextLight size={18} />
    </span>
  );

  const previewPons = async (ty: DraftingType) => {
    try {
      const res = await apis.get(`/drafting/document-types/${encodeURIComponent(ty.document_type_id)}/markdown`);
      const md: string = res?.data?.markdown ?? '';
      const outline = parseOutlineFromMarkdown(md);
      dispatch(showSidePanel({
        type: 'templatePreview',
        showSidePanel: true,
        size: 'lg',
        templateId: ty.document_type_id,
        templateName: humanizeLabel(ty.name),
        isGlobal: true,
        outline,
        description: ty.description,
        message: humanizeLabel(ty.name),
        subMsg: t('PONS template'),
        icon: ponsIcon,
      }));
    } catch {
      dispatch(showSidePanel({
        type: 'templatePreview',
        showSidePanel: true,
        size: 'lg',
        templateId: ty.document_type_id,
        templateName: humanizeLabel(ty.name),
        isGlobal: true,
        outline: [],
        description: ty.description || t('Preview unavailable for this document type.'),
        message: humanizeLabel(ty.name),
        subMsg: t('PONS template'),
        icon: ponsIcon,
      }));
    }
  };

  const previewOwn = async (tmpl: OwnStructureTemplate) => {
    // Prefer the markdown source (same blob layout as PONS templates' template.md)
    // so the side-panel preview is visually identical for both catalogues —
    // properly-formatted headings, lists, bold etc. via MarkdownRenderer.
    // Falls back to the in-memory outline if the endpoint fails or returns
    // an empty override (rare; happens on legacy templates without a
    // generated template_override.md).
    const basePayload = {
      type: 'templatePreview' as const,
      showSidePanel: true,
      width: '70%',
      templateId: tmpl.id,
      templateName: tmpl.TemplateName,
      isGlobal: false,
      message: tmpl.TemplateName,
      subMsg: t('Your template'),
      icon: ownIcon,
    };
    try {
      const res = await apis.get(`/templates/structure/${encodeURIComponent(tmpl.id)}/markdown`);
      const md: string = res?.data?.markdown ?? '';
      const parsedOutline = parseOutlineFromMarkdown(md);
      dispatch(showSidePanel({
        ...basePayload,
        outline: parsedOutline.length > 0 ? parsedOutline : (tmpl.Structure?.outline ?? []),
        description: tmpl.Structure?.description,
      }));
    } catch {
      dispatch(showSidePanel({
        ...basePayload,
        outline: tmpl.Structure?.outline ?? [],
        description: tmpl.Structure?.description,
      }));
    }
  };

  const isSelectedPons = (ty: DraftingType) =>
    tab === 'pons' && !value.structure_template_id && value.document_type_id === ty.document_type_id;
  const isSelectedOwn = (tmpl: OwnStructureTemplate) =>
    tab === 'own' && value.structure_template_id === tmpl.id;

  return (
    <div className="space-y-5">
      {/* Source tabs - the shared glass bar + nav pills, not a hand-rolled
          active-tab treatment. */}
      <div className="glass-bar inline-flex rounded-lg p-0.5">
        <button
          type="button"
          onClick={() => switchTab('pons')}
          className={clsx(
            'nav-pill rounded-md px-3 py-1 text-xs font-medium',
            tab === 'pons' ? 'nav-pill-active text-fg-strong' : 'text-fg-muted',
          )}
        >
          <PiSparkleLight className="me-1 inline h-3 w-3" />
          {t('PONS catalogue')}
          <span className="ms-1 text-[10px] text-fg-muted">({ponsTypes.length})</span>
        </button>
        <button
          type="button"
          onClick={() => switchTab('own')}
          className={clsx(
            'nav-pill rounded-md px-3 py-1 text-xs font-medium',
            tab === 'own' ? 'nav-pill-active text-fg-strong' : 'text-fg-muted',
          )}
        >
          {t('Your templates')}
          <span className="ms-1 text-[10px] text-fg-muted">({ownTemplates.length})</span>
        </button>
      </div>

      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={tab === 'pons' ? t('Search document types…') : t('Search your templates…')}
      />

      {tab === 'pons' && (
        <>
          {ponsCategories.length > 1 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                className={clsx(
                  'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                  FOCUS_RING,
                  activeCategory === null
                    ? SELECTED_CHIP
                    : 'bg-surface-sunken/70 text-fg-muted hover:bg-surface-sunken',
                )}
              >
                {t('All')}
              </button>
              {ponsCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat === activeCategory ? null : cat)}
                  className={clsx(
                    'rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                    FOCUS_RING,
                    activeCategory === cat
                      ? SELECTED_CHIP
                      : 'bg-surface-sunken/70 text-fg-muted hover:bg-surface-sunken',
                  )}
                >
                  {humanizeLabel(cat)}
                </button>
              ))}
            </div>
          )}

          {ponsLoading ? (
            <CardGridSkeleton />
          ) : ponsError ? (
            <p className="text-sm text-warning-700">{t('Could not load PONS catalogue. Try again.')}</p>
          ) : filteredPons.length === 0 ? (
            <p className="text-sm italic text-fg-muted">{t('No document types match your search.')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {filteredPons.map((ty) => (
                <CardItem
                  key={ty.document_type_id}
                  title={humanizeLabel(ty.name)}
                  subtitle={ty.description}
                  selected={isSelectedPons(ty)}
                  isGlobal
                  onSelect={() => selectPons(ty)}
                  onPreview={() => previewPons(ty)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'own' && (
        <>
          {ownLoading ? (
            <CardGridSkeleton />
          ) : filteredOwn.length === 0 ? (
            <p className="text-sm italic text-fg-muted">
              {search ? t('No templates match your search.') : t('You haven\'t created any structure templates yet. Build one from the Templates page.')}
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {filteredOwn.map((tmpl) => {
                const count = tmpl.Structure?.outline?.length ?? 0;
                return (
                  <CardItem
                    key={tmpl.id}
                    title={tmpl.TemplateName}
                    subtitle={tmpl.Structure?.description}
                    badge={count > 0 ? t('{{count}} sections', { count }) : undefined}
                    selected={isSelectedOwn(tmpl)}
                    onSelect={() => selectOwn(tmpl)}
                    onPreview={() => previewOwn(tmpl)}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Tiny helpers (kept inline to avoid sprouting extra files) ────────────────

interface CardItemProps {
  title: string;
  subtitle?: string;
  badge?: string;
  selected: boolean;
  isGlobal?: boolean;
  onSelect: () => void;
  onPreview: () => void;
}

const CardItem: React.FC<CardItemProps> = ({
  title, subtitle, badge, selected, isGlobal, onSelect, onPreview,
}) => {
  const { t } = useTranslation();
  return (
    <Well
      className={clsx(
        'group relative !rounded-xl !p-4 transition-all',
        selected ? SELECTED_WELL : 'hover:bg-surface-sunken',
      )}
    >
      <button type="button" onClick={onSelect} className={clsx('block w-full text-start', FOCUS_RING)}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <PiFileTextLight className="h-4 w-4 text-fg-muted" />
              <span className="truncate text-sm font-medium text-fg-strong">
                {title}
              </span>
              {isGlobal && (
                <span className="inline-flex items-center gap-0.5 text-[10px] capitalize text-fg-muted">
                  <PiSparkleLight className="h-3 w-3" /> PONS
                </span>
              )}
            </div>
            {subtitle && (
              <p className="mt-1 line-clamp-2 text-xs text-fg-muted">{subtitle}</p>
            )}
            {badge && <p className="mt-1 text-[11px] text-fg-muted">{badge}</p>}
          </div>
          {selected && (
            <span className={SELECTED_CHECK_DISC}>
              <PiCheckLight size={11} className="text-ink" />
            </span>
          )}
        </div>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onPreview(); }}
        className={clsx(
          'mt-3 inline-flex items-center gap-1 rounded-md border border-hairline px-2 py-0.5 text-[11px] text-fg-body hover:bg-surface',
          FOCUS_RING,
        )}
      >
        <PiEyeLight className="h-3 w-3" /> {t('Preview')}
      </button>
    </Well>
  );
};

/** Four card-shaped placeholders in the same 2-up grid the results use. */
const CardGridSkeleton: React.FC = () => (
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
    {[1, 2, 3, 4].map((n) => (
      <Skeleton key={n} variant="card" />
    ))}
  </div>
);

/**
 * Parse `# / ## ` headings from raw markdown into the outline shape that
 * TemplatePreviewPanel already renders. Mirrors backend's
 * `_outline_from_markdown` so users see a consistent preview no matter
 * which source path the markdown came through.
 */
function parseOutlineFromMarkdown(md: string): Array<{ title: string; prompt: string }> {
  if (!md) return [];
  const lines = md.split(/\r?\n/);
  const out: Array<{ title: string; prompt: string }> = [];
  let title: string | null = null;
  let body: string[] = [];
  for (const line of lines) {
    const stripped = line.replace(/^\s+/, '');
    if (stripped.startsWith('## ')) {
      if (title !== null) out.push({ title, prompt: body.join('\n').trim() });
      title = stripped.slice(3).trim();
      body = [];
    } else if (stripped.startsWith('# ') && title === null) {
      // Treat first H1 as the doc title - skip.
      continue;
    } else if (title !== null) {
      body.push(line);
    }
  }
  if (title !== null) out.push({ title, prompt: body.join('\n').trim() });
  return out;
}

export default ChooseTemplateStep;
